// Input + anchor validation for the `context` command. Pattern globbing runs
// against the local checkout so a bad anchor is caught before the API call.
import { looksLikeDocument, scanWritePayload, formatSecretFindings } from '@driftless/topic-schema'
import { getRepoRoot } from '../../git'
import { collectMulti } from './args'
import { MAX_FIELD_SIZE, OVERBROAD_PATTERN_THRESHOLD, NOISE_PATH, type PatternValidationStats } from './types'

export function toSlug(name: string): string {
  return name.toLowerCase().replace(/\s+/g, '-')
}

export function checkFieldSize(label: string, value: string | undefined, limit = MAX_FIELD_SIZE): void {
  if (value && value.length > limit) {
    console.error(`Error: ${label} exceeds ${limit / 1024} KB (${value.length} chars). Split into multiple ${label} invocations.`)
    process.exit(1)
  }
}

export function checkRel(r: string): { relation_type: string; to_slug: string } {
  const idx = r.indexOf(':')
  if (idx === -1) {
    console.error(`Error: --rel must be "type:slug", got "${r}"`)
    process.exit(1)
  }
  const relation_type = r.slice(0, idx)
  const to_slug = r.slice(idx + 1)
  if (!relation_type || !to_slug) {
    console.error(`Error: --rel must be "type:slug", got "${r}" (empty type or slug)`)
    process.exit(1)
  }
  return { relation_type, to_slug }
}

export function hasCommaOutsideBraces(pattern: string): boolean {
  let depth = 0
  for (const ch of pattern) {
    if (ch === '{') { depth++; continue }
    if (ch === '}') { depth = Math.max(0, depth - 1); continue }
    if (ch === ',' && depth === 0) return true
  }
  return false
}

// On CREATE, merge a plural replace value (`--gotchas`) with singular append
// values (`--gotcha` ...) into one newline-joined field. On a fresh topic
// append == replace, so both forms should contribute instead of the singular
// ones being silently dropped. Returns undefined when nothing was provided.
export function mergeAppendField(plural: string | boolean | undefined, singular: string[]): string | undefined {
  return [typeof plural === 'string' ? plural : null, ...singular].filter(Boolean).join('\n') || undefined
}

export function warnIfConflictingAppend(args: string[], flags: Record<string, string | boolean>, appendKey: string, replaceKey: string): void {
  const hasAppend = collectMulti(args, [`--${appendKey}`]).length > 0
  const hasReplace = flags[replaceKey]
  if (hasAppend && hasReplace) {
    console.error(`Warning: both --${appendKey} (append) and --${replaceKey} (replace) provided. The replace will overwrite the whole field; append values are also sent separately.`)
  }
}

function inspectPattern(pattern: string): { matchCount: number; samples: string[]; suspiciousAll: boolean } {
  try {
    const { globSync } = require('node:fs') as typeof import('node:fs')
    const base = getRepoRoot() ?? process.cwd()
    const matches = (globSync(pattern, { cwd: base }) as string[]).map((m) => m.replace(/\\/g, '/'))
    const realMatches = matches.filter((m) => !NOISE_PATH.test(m))
    const suspiciousAll = matches.length > 0 && realMatches.length === 0
    return {
      matchCount: realMatches.length,
      samples: realMatches.slice(0, 3),
      suspiciousAll,
    }
  } catch {
    return { matchCount: 0, samples: [], suspiciousAll: false }
  }
}

export function countLocalFilesMatching(pattern: string): number {
  return inspectPattern(pattern).matchCount
}

export function estimateAnchorBreadth(patterns: string[]): number {
  let total = 0
  for (const p of patterns) total += countLocalFilesMatching(p)
  return total
}

// Print per-pattern match counts + warnings. Called at write time on add
// and update. Returns ok=false when ANY pattern matched 0 real files
// (caller decides whether to block) plus aggregated stats for analytics.
export function validateAndReportPatterns(patterns: string[]): PatternValidationStats {
  const stats: PatternValidationStats = {
    ok: true,
    pattern_count: patterns.length,
    zero_match_count: 0,
    noise_only_count: 0,
    over_broad_count: 0,
    max_match_count: 0,
  }
  if (patterns.length === 0) return stats
  const repoRoot = getRepoRoot()
  if (repoRoot && repoRoot !== process.cwd()) {
    console.error(`  Patterns are relative to repo root (${repoRoot}), not your current directory.`)
  }
  for (const p of patterns) {
    const { matchCount, samples, suspiciousAll } = inspectPattern(p)
    stats.max_match_count = Math.max(stats.max_match_count, matchCount)
    if (matchCount === 0) {
      if (suspiciousAll) {
        stats.noise_only_count++
        console.error(`  ⚠ pattern "${p}" matches ${0} files (all matches were under node_modules/dist/.git/etc — likely unintended).`)
      } else {
        stats.zero_match_count++
        console.error(`  ⚠ pattern "${p}" matches 0 files locally.`)
      }
      stats.ok = false
      continue
    }
    const preview = samples.length > 0 ? ` — e.g. ${samples.join(', ')}${matchCount > samples.length ? `, +${matchCount - samples.length} more` : ''}` : ''
    if (matchCount > OVERBROAD_PATTERN_THRESHOLD) {
      stats.over_broad_count++
      console.error(`  ⚠ pattern "${p}" matches ${matchCount} files${preview}`)
      console.error(`    Healthy topics anchor ~5–40 files; > ${OVERBROAD_PATTERN_THRESHOLD} usually means the glob is too wide.`)
    } else {
      console.log(`  ✓ pattern "${p}" matches ${matchCount} file${matchCount === 1 ? '' : 's'}${preview}`)
    }
  }
  return stats
}

// W1 — print the server's vault_validation envelope (rules B/F/C) the same way
// local anchor warnings print: non-blocking stderr at the friction point. The
// SERVER computes the violations (one implementation for CLI/MCP/dashboard);
// the CLI only renders them.
export function printVaultValidation(result: unknown): void {
  const violations = (result as { vault_validation?: Array<{ rule: string; field: string; evidence: string; fix_hint: string }> })?.vault_validation
  if (!Array.isArray(violations) || violations.length === 0) return
  console.error('')
  for (const v of violations) {
    console.error(`  ⚠ vault rule ${v.rule} (${v.field}): ${v.evidence}`)
    console.error(`    ${v.fix_hint}`)
  }
}

export function warnIfOverBroadAnchor(patterns: string[]): void {
  if (patterns.length === 0) return
  const breadth = estimateAnchorBreadth(patterns)
  if (breadth <= OVERBROAD_PATTERN_THRESHOLD) return
  console.error('')
  console.error(`  ⚠ over-broad anchor: this topic now covers ${breadth} files.`)
  console.error(`    Healthy topics anchor ~5–40 files; > ${OVERBROAD_PATTERN_THRESHOLD} usually means the glob is too wide.`)
  console.error(`    Consider splitting into narrower topics, or tighten with --remove-pattern.`)
}

// Secret hygiene at the CLI friction point: scan the EXACT request payload
// locally BEFORE the API call, so a pasted credential never leaves the machine
// on this path. `payload` is the request body that's about to be sent — pass
// the same object you transmit, and scanWritePayload picks the canonical text
// fields (the single source shared with the server gate). Blocks unless the
// author passed --allow-secrets. The API runs the same scan as the
// authoritative gate (covers MCP/agent/raw clients).
// Read the --allow-secrets override. parseArgs yields boolean `true` for a bare
// flag (the only valid form — there is no `=value` parsing, and a `--flag=x`
// token would be rejected as an unknown flag). A stray value token captured as
// the flag's value (e.g. `--allow-secrets ./key.pem`) is NOT an override —
// fail safe, keep the scan on.
export function isAllowSecrets(flags: Record<string, string | boolean>): boolean {
  return flags['allow-secrets'] === true
}

export function checkSecretsOrExit(
  payload: Record<string, unknown>,
  allowSecrets: boolean,
): void {
  if (allowSecrets) return
  const findings = scanWritePayload(payload)
  if (findings.length === 0) return
  console.error('')
  console.error(`  ⛔ possible secret(s) detected — ${formatSecretFindings(findings)}`)
  console.error(`    Remove the secret, or re-run with --allow-secrets to write anyway.`)
  process.exit(1)
}

// Content-first nudge at the write friction point: --how / --what are SHORT
// fields. When a value looks like a whole document (headings, a table, or very
// long), tell the author to move the long-form into --content. Non-blocking.
export function warnIfDocShapedField(flags: Record<string, string | boolean>): void {
  for (const field of ['how', 'what'] as const) {
    const value = flags[field]
    if (typeof value === 'string' && looksLikeDocument(value)) {
      console.error('')
      console.error(`  ⚠ --${field} looks like a full document (headings/table/very long).`)
      console.error(`    --${field} is a short 1–3 sentence field; put the long-form in --content so it renders as a doc.`)
    }
  }
}
